import { useMsal } from '@azure/msal-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layout,
  Typography,
  Carousel,
  Breadcrumb,
  Row,
  Col,
  Button,
  Spin,
  message,
  Card,
  Modal
} from 'antd';
import { HomeOutlined, CalendarOutlined, LeftOutlined, RightOutlined, TrophyOutlined, AlignCenterOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { loginRequest } from '../../authConfig';
import type { CarouselRef } from 'antd/es/carousel';
import { AlignCenter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchWithRetry } from '../utils/fetchWithRetry';


const { Content } = Layout;
const { Title, Text } = Typography;


const DRIVE_ID = 'b!RE4_DsJJCUy-m8eM4yQwj1AGB-6wIDZMoWcyYivqWiILIwCLC7Z9S5p5YVdvX5xh';
const AWARDS_FOLDER_ID = '01LUXCZVFRMXHEEQTCEJGZGKVO35ZXLL36';

// const DRIVE_ID = 'b!r-2Ea86KVkOEAIiKcZ4wkSCq-B4Pf15MtG2CMR-4nuu1nXaFQOLdRZck_NgW6GOQ';
// const AWARDS_FOLDER_ID = '01RGY26YCFN4KQYQKO5VG25A5Z62HSIYVG';

interface CategoryFolder {
  key: string;
  label: string;
  id: string;
}

interface YearFolder {
  id: string;
  name: string;
  folder: any;
}

interface GalleryImage {
  id: string;
  name: string;
  src: string;
  caption?: string;
  categoryKey?: string;
  year?: string;
  isPresentation?: boolean;
  webUrl?: string;
  parentFolderUrl?: string;
}

interface GraphApiResponse {
  value: any[];
  '@microsoft.graph.downloadUrl'?: string;
  webUrl?: string;
}



const WallOfFamePage = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const carouselRef = useRef<CarouselRef>(null);

  const [categoryFolders, setCategoryFolders] = useState<CategoryFolder[]>([]);
  const [yearFolders, setYearFolders] = useState<YearFolder[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [showAllYears, setShowAllYears] = useState<boolean>(false);
  const [showAllCategories, setShowAllCategories] = useState<boolean>(false);
  const [isYearModalVisible, setIsYearModalVisible] = useState<boolean>(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState<boolean>(false);
  const { t } = useTranslation();

  const toggleYearModal = () => {
    setIsYearModalVisible(!isYearModalVisible);
  };

  const toggleCategoryModal = () => {
    setIsCategoryModalVisible(!isCategoryModalVisible);
  };

  // Carousel navigation functions
  const handlePrev = () => {
    if (carouselRef.current) {
      carouselRef.current.prev();
    }
  };

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };

  // Integrated getAccessToken method
  const getAccessToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: instance.getAllAccounts()[0]
      });
      return response.accessToken;
    } catch (silentError) {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["User.Read", 'Sites.Read.All'],
          account: instance.getAllAccounts()[0]
        });
        return response.accessToken;
      } catch (error) {
        console.error('Error acquiring token:', error);
        throw error;
      }
    }
  }, [instance]);

  const fetchFromGraph = async (url: string): Promise<GraphApiResponse> => {
    try {
      const accessToken = await getAccessToken();

      const graphResponse = await fetchWithRetry(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (!graphResponse.ok) {
        throw new Error(`Error fetching from SharePoint: ${graphResponse.statusText}`);
      }

      return await graphResponse.json();
    } catch (error) {
      console.error('Error in fetchFromGraph:', error);
      if (error instanceof Error && error.message.includes('consent')) {
        message.error('Application permissions need to be granted by an administrator');
      }
      throw error;
    }
  };

  // Load category folders
  const loadCategoryFolders = async () => {
    setLoading(true);
    try {
      const url = `https://graph.microsoft.com/v1.0/drives/${DRIVE_ID}/items/${AWARDS_FOLDER_ID}/children`;
      const data = await fetchFromGraph(url);
      const folders = data.value.filter((item: any) => item.folder);

      const categoryItems: CategoryFolder[] = [];

      folders.forEach((folder: any) => {
        categoryItems.push({
          key: folder.name.toLowerCase().replace(/\s+/g, ''),
          label: folder.name,
          id: folder.id
        });
      });

      setCategoryFolders(categoryItems);

      // Load default category data if we have folders
      if (folders.length > 0) {
        // Select the first category by default
        const firstCategory = categoryItems[1];
        setSelectedCategory(firstCategory.key);
        // Load years for the first category
        const yearFolders = await loadYearFolders(firstCategory.id);

        // Load images for the first year of the first category
        if (yearFolders.length > 0) {
          const firstYear = yearFolders[0];
          setSelectedYear(firstYear.name);

          const images = await loadImagesFromFolder(firstYear.id, firstYear.folder.webUrl);
          setGalleryImages(images.map(img => ({
            ...img,
            caption: `${firstCategory.label} ${firstYear.name}`,
            categoryKey: firstCategory.key,
            year: firstYear.name
          })));
        }
      }
    } catch (err) {
      console.error('Error loading category folders:', err);
      setError('Failed to load award categories');
    } finally {
      setLoading(false);
    }
  };


  // Load year folders for a specific category
  const loadYearFolders = async (categoryId: string): Promise<YearFolder[]> => {
    try {
      const url = `https://graph.microsoft.com/v1.0/drives/${DRIVE_ID}/items/${categoryId}/children`;
      const data = await fetchFromGraph(url);

      // Filter to only include folder items, expecting year folders
      const folders = data.value
        .filter((item: any) => item.folder)
        .sort((a: any, b: any) => b.name.localeCompare(a.name)); // Sort years in descending order

      setYearFolders(folders);

      // If we have year folders, select the most recent by default
      if (folders.length > 0) {
        setSelectedYear(folders[0].name);
      }

      return folders;
    } catch (err) {
      console.error('Error loading year folders:', err);
      setError('Failed to load year data');
      return [];
    }
  };

  // Load images from all categories for the carousel
  const loadAllCategoryImages = async () => {
    try {
      const allImages: GalleryImage[] = [];

      // Get each category except "All"
      const categories = categoryFolders.filter(cat => cat.key !== 'all');

      // For each category, get the most recent year's images
      for (const category of categories) {
        const yearFolders = await loadYearFolders(category.id);

        if (yearFolders.length > 0) {
          const mostRecentYear = yearFolders[0];
          const images = await loadImagesFromFolder(mostRecentYear.id, mostRecentYear.folder.webUrl);

          // Add category and year info to each image
          const processedImages = images.map(img => ({
            ...img,
            caption: `${category.label} ${mostRecentYear.name}`,
            categoryKey: category.key,
            year: mostRecentYear.name
          }));

          allImages.push(...processedImages);
        }
      }

      setGalleryImages(allImages);
    } catch (err) {
      console.error('Error loading all category images:', err);
      setError('Failed to load gallery images');
    }
  };

  // Load files from a specific folder
  const loadImagesFromFolder = async (folderId: string, folderUrl: string): Promise<GalleryImage[]> => {
    try {
      const url = `https://graph.microsoft.com/v1.0/drives/${DRIVE_ID}/items/${folderId}/children`;
      const data = await fetchFromGraph(url);


      const supportedExtensions = ['.pptx', '.ppt', '.jpg', '.jpeg', '.png', '.gif'];
      const files = data.value.filter((item: any) => {
        if (!item.file) return false;
        const ext = item.name.substr(item.name.lastIndexOf('.')).toLowerCase();
        return supportedExtensions.includes(ext);
      });

      if (files.length === 0) {
        return [];
      }

      // For each file, get the download URL and SharePoint URL
      const processedImages: GalleryImage[] = [];

      // Process each file sequentially to avoid overwhelming the API
      for (const file of files) {
        try {
          // Check if we already have the download URL in the response
          if (file['@microsoft.graph.downloadUrl']) {
            processedImages.push({
              id: file.id,
              name: file.name,
              src: file['@microsoft.graph.downloadUrl'],
              webUrl: file.webUrl,
              parentFolderUrl: folderUrl,
              isPresentation: file.name.toLowerCase().endsWith('.pptx') || file.name.toLowerCase().endsWith('.ppt')
            });
            continue;
          }

          // If not, fetch it specifically with webUrl included
          const fileDetailsUrl = `https://graph.microsoft.com/v1.0/drives/${DRIVE_ID}/items/${file.id}?$select=id,name,webUrl,@microsoft.graph.downloadUrl`;
          const fileDetails = await fetchFromGraph(fileDetailsUrl);

          if (fileDetails && fileDetails['@microsoft.graph.downloadUrl']) {
            processedImages.push({
              id: file.id,
              name: file.name,
              src: fileDetails['@microsoft.graph.downloadUrl'],
              webUrl: fileDetails.webUrl,
              parentFolderUrl: folderUrl,
              isPresentation: file.name.toLowerCase().endsWith('.pptx') || file.name.toLowerCase().endsWith('.ppt')
            });
          }
        } catch (err) {
          console.error(`Error getting details for file ${file.name}:`, err);
        }
      }
      return processedImages;
    } catch (err) {
      console.error('Error loading files from folder:', err);
      return [];
    }
  };

  // Modified to redirect to parent folder instead of the image itself
// Updated handleImageClick function for WallOfFame component
const handleImageClick = (image: GalleryImage) => {
  // Case 1: If we already have the parent folder URL, use it directly
  if (image.parentFolderUrl) {
    window.open(image.parentFolderUrl, '_blank');
    return;
  }
  
  // Case 2: If we have a webUrl but no parentFolderUrl, try to extract the parent folder
  if (image.webUrl) {
    try {
      // Extract the folder path by removing the filename from the URL
      const urlObj = new URL(image.webUrl);
      const pathParts = urlObj.pathname.split('/');
      
      // Remove the file name (last part) from the path
      pathParts.pop();
      const folderPath = pathParts.join('/');
      
      // Reconstruct the folder URL
      const folderUrl = `${urlObj.origin}${folderPath}`;
      window.open(folderUrl, '_blank');
    } catch (error) {
      // If extraction fails, fallback to the original webUrl
      console.error('Error extracting folder URL:', error);
      window.open(image.webUrl, '_blank');
    }
    return;
  }
  
  // Case 3: Last resort - use the direct image source URL if nothing else is available
  if (image.src) {
    window.open(image.src, '_blank');
  }
};



  // Handle category selection
  const handleCategoryClick = async (categoryKey: string) => {
    setLoading(true);
    setSelectedCategory(categoryKey);

    try {
      const category = categoryFolders.find(cat => cat.key === categoryKey);

      if (!category) return;

      if (categoryKey === 'all') {
        // Load all categories
        await loadYearFolders(AWARDS_FOLDER_ID);
        await loadAllCategoryImages();
      } else {
        // Load specific category
        const yearFolders = await loadYearFolders(category.id);

        if (yearFolders.length > 0) {
          // Select the most recent year by default
          const mostRecentYear = yearFolders[0];
          setSelectedYear(mostRecentYear.name);

          // Load images for this year
          const images = await loadImagesFromFolder(mostRecentYear.id, mostRecentYear.folder.webUrl);
          setGalleryImages(images.map(img => ({
            ...img,
            caption: `${category.label} ${mostRecentYear.name}`,
            categoryKey: category.key,
            year: mostRecentYear.name
          })));
        } else {
          setGalleryImages([]);
        }
      }
    } catch (err) {
      console.error('Error handling category click:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle year selection
  const handleYearClick = async (year: string) => {
    setLoading(true);

    // Toggle selection if clicking on the same year
    if (year === selectedYear) {
      setSelectedYear(null);
      await loadAllCategoryImages();
      setLoading(false);
      return;
    }

    setSelectedYear(year);

    try {
      // Find the category folder ID
      const category = categoryFolders.find(cat => cat.key === selectedCategory);
      if (!category) return;

      // Find the year folder for this category
      const yearFolder = yearFolders.find(y => y.name === year);
      if (!yearFolder) return;

      // Load images for this specific year and category
      const images = await loadImagesFromFolder(yearFolder.id, yearFolder.folder.webUrl);

      setGalleryImages(images.map(img => ({
        ...img,
        caption: `${category.label} ${year}`,
        categoryKey: category.key,
        year
      })));
    } catch (err) {
      console.error('Error handling year click:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    loadCategoryFolders();

    const tokenExpiryWarning = () => {
      message.warning(
        'Your session will expire soon. You may need to refresh the page.',
        10
      );
    };


    // const tokenTimer = setTimeout(tokenExpiryWarning, 3300000);

    // return () => {
    //   clearTimeout(tokenTimer);
    // };
  }, []);

  return (
    <Layout style={{ background: 'linear-gradient(135deg, #f6f8fd 0%, #f1f1fe 100%)', height: '100%' }}>
      <AppHeaderHomePageTemplate />

      <div style={{ padding: '8px 24px' }}>
        <Breadcrumb style={{ marginTop: '12px' }} separator="/">
          <Breadcrumb.Item>
            <Link to="/home">
              <HomeOutlined style={{ marginRight: 4 }} />
              <span>{t('home')}</span>
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t('walloffame')}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Content style={{
        padding: '0 12px',
        maxWidth: '100%',
        margin: '0 auto',
        overflow: 'auto',
      }}>
        {loading && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1000
  }}>
    <Spin size="large" />
  </div>
)}

        {error && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            background: 'rgba(254,226,226,0.5)',
            border: '1px solid #FCA5A5',
            borderRadius: '12px',
            margin: '20px 0'
          }}>
            <Text style={{ color: '#DC2626', fontSize: '16px' }}>{error}</Text>
          </div>
        )}

        {!loading && !error && (
          <Row gutter={[16, 20]} style={{ margin: '20px 0 0 0' }}>
            {/* Main Left Column - Hero Section */}
            <Col xs={24} lg={18}>
              <Card
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  marginBottom: '8px',
                  height: 'auto',
                  minHeight: '500px',
                  maxHeight: '80vh'
                }}
                bodyStyle={{ padding: 0 }}
              >
                {/* Inline Title and Icon */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 24px',
                  borderBottom: '1px solid #f0f0f7',
                  background: 'linear-gradient(135deg, #f9f7ff 0%, #f0f0ff 100%)'
                }}>
                  <TrophyOutlined style={{
                    fontSize: '24px',
                    color: '#8B5CF6',
                    marginRight: '12px'
                  }} />
                  <Title
                    level={3}
                    style={{
                      color: '#4B5563',
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('walloffame')}
                  </Title>
                  <Text style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    marginLeft: '16px'
                  }}>
                    {t('celebrating_excellence')}
                  </Text>
                </div>

                {/* Hero Carousel */}
                <div style={{ position: 'relative' }}>
                  {galleryImages.length > 0 ? (
                    <div style={{
                      height: '100%',
                    }}>
                      <Carousel
                        autoplay
                        effect="fade"
                        autoplaySpeed={3000}
                        style={{ height: '100%', maxHeight: '700px' }}
                        arrows={false}
                        ref={carouselRef}
                      >
                        {galleryImages.map((image, index) => (
                          <div key={index} style={{
                            height: '600px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '20px',
                            boxSizing: 'border-box'
                          }}>
                            {image.isPresentation ? (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background: 'linear-gradient(135deg, #f9f7ff 0%, #f5f5f5 100%)',
                                padding: '20px'
                              }}>
                                <div style={{
                                  fontSize: '64px',
                                  color: '#8B5CF6',
                                  marginBottom: '24px',
                                  filter: 'drop-shadow(0 4px 8px rgba(139,92,246,0.3))'
                                }}>
                                  📊
                                </div>
                                <Text style={{
                                  fontSize: '24px',
                                  fontWeight: 'bold',
                                  maxWidth: '80%',
                                  textAlign: 'center',
                                  color: '#4B5563'
                                }}>
                                  {image.name}
                                </Text>
                              </div>
                            ) : (
                              <img
                                src={image.src}
                                alt={image.caption || `Image ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '520px',
                                  objectFit: 'contain',
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleImageClick(image)}
                              />
                            )}
                            {image.caption && (
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 100%)',
                                color: 'white',
                                padding: '20px 16px 16px',
                                textAlign: 'center'
                              }}>
                                <Text style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                                  {image.caption}
                                </Text>
                                {!image.isPresentation && (
                                  <div style={{ fontSize: '12px', marginTop: '4px', color: '#e0e0e0' }}>
                                    Click to view in folder
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </Carousel>
                      <div 
                        className="carousel-nav carousel-prev" 
                        aria-label="Previous" 
                        onClick={handlePrev}
                        style={{left: '0px', width: '50px'}}
                      >
                        <LeftOutlined style={{fontSize: '20px', color: 'white'}}/>
                      </div>
                      <div 
                        className="carousel-nav carousel-next" 
                        aria-label="Next" 
                        onClick={handleNext}
                        style={{right: '0px', width: '50px'}}
                      >
                        <RightOutlined style={{fontSize: '20px', color: 'white'}}/>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Text style={{ color: '#6B7280' }}>No images available</Text>
                    </div>
                  )}
                </div>
              </Card>

              <div className="category-container" style={{ display: 'none' }}>
                <Card
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}
                  bodyStyle={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f9f7ff 0%, #f0f0ff 100%)'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: 'center'
                  }}>
                    {categoryFolders.map((category) => (
                      <Button
                        key={category.key}
                        type={selectedCategory === category.key ? 'primary' : 'default'}
                        shape="round"
                        style={{
                          backgroundColor: selectedCategory === category.key ? '#8B5CF6' : 'white',
                          borderColor: selectedCategory === category.key ? '#8B5CF6' : '#d9d9d9',
                          color: selectedCategory === category.key ? 'white' : 'rgba(0, 0, 0, 0.85)',
                          fontWeight: selectedCategory === category.key ? 'bold' : 'normal',
                          height: '36px',
                          padding: '0 16px',
                          boxShadow: selectedCategory === category.key ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                          transition: 'all 0.3s'
                        }}
                        onClick={() => handleCategoryClick(category.key)}
                      >
                        {category.label}
                      </Button>
                    ))}
                  </div>
                </Card>
              </div>
            </Col>

            {/* Right Column - Categories and Years */}
            <Col xs={24} lg={6}>
              <Card
                style={{
                  marginBottom: '20px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  position: 'sticky',
                  top: '20px'
                }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8B5CF6' }}>
                    <TrophyOutlined style={{ marginRight: '8px' }} />
                    <Text strong style={{ fontSize: '14px', color: '#8B5CF6', margin: 0 }}>
                    {t('award_categories')}
                    </Text>
                  </div>
                }
                headStyle={{
                  background: 'linear-gradient(135deg, #f9f7ff 0%, #f0f0ff 100%)',
                  borderBottom: '1px solid #e9e9ff',
                  padding: '10px 16px'
                }}
                bodyStyle={{
                  padding: '12px',
                  background: 'white',
                  maxHeight: '210px',
                  overflowY: 'auto'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categoryFolders.slice(0, 3).map((category) => (
                    <Button
                      key={category.key}
                      type={selectedCategory === category.key ? 'primary' : 'default'}
                      shape="round"
                      style={{
                        backgroundColor: selectedCategory === category.key ? '#8B5CF6' : 'white',
                        borderColor: selectedCategory === category.key ? '#8B5CF6' : '#d9d9d9',
                        color: selectedCategory === category.key ? 'white' : 'rgba(0, 0, 0, 0.85)',
                        fontWeight: selectedCategory === category.key ? 'bold' : 'normal',
                        height: '36px',
                        boxShadow: selectedCategory === category.key ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                        transition: 'all 0.3s'
                      }}
                      onClick={() => handleCategoryClick(category.key)}
                    >
                      {category.label}
                    </Button>
                  ))}

                  {categoryFolders.length > 3 && (
                    <Button
                      type="link"
                      onClick={toggleCategoryModal}
                      style={{
                        color: '#8B5CF6',
                        fontWeight: 'bold',
                        height: '36px',
                        textAlign: 'center'
                      }}
                    >
                      {t('show_more')}
                    </Button>
                  )}
                </div>
              </Card>

              {/* Year Filter Card */}
              <Card
                style={{
                  marginBottom: '20px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  position: 'sticky',
                  top: '240px'
                }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8B5CF6' }}>
                    <CalendarOutlined style={{ marginRight: '8px' }} />
                    <Text strong style={{ fontSize: '14px', color: '#8B5CF6', margin: 0 }}>
                    {t('filter_by_year')}
                    </Text>
                  </div>
                }
                headStyle={{
                  background: 'linear-gradient(135deg, #f9f7ff 0%, #f0f0ff 100%)',
                  borderBottom: '1px solid #e9e9ff',
                  padding: '10px 16px'
                }}
                bodyStyle={{
                  padding: '12px',
                  background: 'white',
                  maxHeight: '210px',
                  overflowY: 'auto'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {yearFolders.slice(0, 3).map(year => (
                    <Button
                      key={year.name}
                      type={selectedYear === year.name ? 'primary' : 'default'}
                      shape="round"
                      style={{
                        backgroundColor: selectedYear === year.name ? '#8B5CF6' : 'white',
                        borderColor: selectedYear === year.name ? '#8B5CF6' : '#d9d9d9',
                        color: selectedYear === year.name ? 'white' : 'rgba(0, 0, 0, 0.85)',
                        textAlign: 'center',
                        fontWeight: selectedYear === year.name ? 'bold' : 'normal',
                        height: '36px',
                        boxShadow: selectedYear === year.name ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                        transition: 'all 0.3s'
                      }}
                      onClick={() => handleYearClick(year.name)}
                    >
                      {year.name}
                    </Button>
                  ))}

                  {yearFolders.length > 3 && (
                    <Button
                      type="link"
                      onClick={toggleYearModal}
                      style={{
                        color: '#8B5CF6',
                        fontWeight: 'bold',
                        height: '36px',
                        textAlign: 'center'
                      }}
                    >
                      Show More...
                    </Button>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Modals */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CalendarOutlined style={{ marginRight: '8px', color: '#8B5CF6' }} />
              <span>Select Year</span>
            </div>
          }
          open={isYearModalVisible}
          onCancel={toggleYearModal}
          footer={null}
          width={400}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '10px',
            padding: '10px'
          }}>
            {yearFolders.map(year => (
              <Button
                key={year.name}
                type={selectedYear === year.name ? 'primary' : 'default'}
                shape="round"
                style={{
                  backgroundColor: selectedYear === year.name ? '#8B5CF6' : 'white',
                  borderColor: selectedYear === year.name ? '#8B5CF6' : '#d9d9d9',
                  color: selectedYear === year.name ? 'white' : 'rgba(0, 0, 0, 0.85)',
                  fontWeight: selectedYear === year.name ? 'bold' : 'normal',
                  margin: '5px'
                }}
                onClick={() => {
                  handleYearClick(year.name);
                  toggleYearModal();
                }}
              >
                {year.name}
              </Button>
            ))}
          </div>
        </Modal>

        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TrophyOutlined style={{ marginRight: '8px', color: '#8B5CF6' }} />
              <span>Select Category</span>
            </div>
          }
          open={isCategoryModalVisible}
          onCancel={toggleCategoryModal}
          footer={null}
          width={400}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '10px',
            padding: '10px'
          }}>
            {categoryFolders.map((category) => (
              <Button
                key={category.key}
                type={selectedCategory === category.key ? 'primary' : 'default'}
                shape="round"
                style={{
                  backgroundColor: selectedCategory === category.key ? '#8B5CF6' : 'white',
                  borderColor: selectedCategory === category.key ? '#8B5CF6' : '#d9d9d9',
                  color: selectedCategory === category.key ? 'white' : 'rgba(0, 0, 0, 0.85)',
                  fontWeight: selectedCategory === category.key ? 'bold' : 'normal',
                  margin: '5px'
                }}
                onClick={() => {
                  handleCategoryClick(category.key);
                  toggleCategoryModal();
                }}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </Modal>
      </Content>

      <style>{`
      .ant-carousel .slick-slider {
        position: relative;
      }

      .carousel-nav {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 50px; /* Increased from 20px to 50px */
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 1;
        transition: background-color 0.3s ease;
      }

      .carousel-nav:hover {
        background-color: rgba(0, 0, 0, 0.7);
      }

      .carousel-prev {
        left: 0;
      }

      .carousel-next {
        right: 0;
      }

      .carousel-arrow {
        color: white;
        font-size: 20px;
      }


      .ant-carousel .slick-prev,
      .ant-carousel .slick-next {
        display: none !important; /* Hide default buttons */
      }


        .ant-layout {
        min-height: 100vh;
        overflow-x: hidden;
        margin: 0;
        padding: 0;
      }

      .ant-layout-content {
        max-width: 1800px;
        margin: 0 auto;
        padding: 0 8px !important;
      }


      .ant-card {
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        margin-bottom: 16px !important; /* Reduced margin */
      }

      .ant-card-head {
        background: linear-gradient(135deg, #f9f7ff 0%, #f0f0ff 100%);
        border-bottom: 1px solid #e9e9ff;
        padding: 10px 16px;
      }

      .category-button, .year-button {
        transition: none;
      }

      .category-button:hover, .year-button:hover {
        transform: none;
        box-shadow: none;
      }

      .gallery-image {
        width: 100%;
        height: 520px;
        object-fit: contain;
        cursor: pointer;
        transition: none;
      }

      .gallery-image:hover {
        transform: none;
      }

      .image-caption {
        position: absolute;
        bottom: 0;
        width: 100%;
        background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 100%);
        color: white;
        padding: 20px 16px 16px;
        text-align: center;
      }


      @media (min-width: 1600px) {
        .ant-layout-content {
          max-width: 1800px;
          margin: 0 auto;
        }

        .carousel-container {
          height: 70vh;
        }
      }

      @media (max-width: 1600px) {
        .ant-layout-content {
          max-width: 1400px;
        }

        .carousel-container {
          height: 65vh;
        }
      }

      @media (max-width: 1200px) {
        .ant-layout-content {
          max-width: 100%;
          padding: 0 12px;
        }

        .carousel-container {
          height: 60vh;
        }
      }

      @media (max-width: 992px) {
        .category-container {
          display: block !important;
          margin-bottom: 16px;
        }

        .carousel-container {
          height: 50vh;
        }
      }


      @media screen and (min-resolution: 120dpi) {
        .ant-card {
          transform: none;
          transform-origin: top left;
        }

        .ant-modal {
          transform: none;
        }
      }

      @media screen and (min-resolution: 144dpi) {
        .ant-card {
          transform: none;
          transform-origin: top left;
        }

        .ant-modal {
          transform: none;
        }
      }


      @media (max-width: 768px) {
        .ant-card-head-title {
          font-size: 14px;
        }

        .ant-btn {
          font-size: 12px;
          height: 32px;
        }
      }

      body {
        margin: 0;
        padding: 0;
      }

      #root {
        margin: 0;
        padding: 0;
      }
      `}</style>
    </Layout>
  );
};

export default WallOfFamePage;